class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.resetButton = this.querySelector('button[type="reset"], .predictive-search__clear');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pendingSearchTimeout = null;
    this.searchDebounceDelay = 300;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.setupResetButton();
    
    // ✅ NEW: Prevent clicks inside results from closing modal
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (this.pendingSearchTimeout) {
      clearTimeout(this.pendingSearchTimeout);
      this.pendingSearchTimeout = null;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.resetButton) {
      if (this.input.value.trim() === '') {
        this.resetButton.classList.add('hidden');
      } else {
        this.resetButton.classList.remove('hidden');
      }
    }

    this.pendingSearchTimeout = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, this.searchDebounceDelay);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();

      if (this.pendingSearchTimeout) {
        clearTimeout(this.pendingSearchTimeout);
        this.pendingSearchTimeout = null;
      }

      this.closeResults(true);
      this.querySelector('#predictive-search-option-search-keywords')?.classList.add('hidden');
      this.querySelector('.search_tab_content')?.classList.add('hidden');
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
      this.input.click();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  setupResetButton() {
    const resetButton = this.querySelector('.reset__button');
    const input = this.querySelector('input[type="search"]');
    const results = this.querySelector('[data-predictive-search]');

    if (resetButton && input && results) {
      resetButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // ✅ Prevent modal from closing
        input.value = '';
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();

        results.innerHTML = `<div class="predictive-search__loading-state">
          <p>Start typing to search...</p>
          <button class="reset__button">Reset</button>
        </div>`;
      });
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Check cache first - instant results!
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Abort previous request if still pending
    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // Cache results across all instances
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // Ignore abort errors (code 20) - these are intentional
        if (error?.code === 20 || error.name === 'AbortError') return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.loaderStartTime = Date.now();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();

    if (!this.predictiveSearchResults.querySelector('.predictive-search__result-group')) {
      this.predictiveSearchResults.innerHTML = `<div class="no-result">
        <p>No Result Found</p>
        <button
        type="reset"
        class="field__button reset__button btn btn-primary"
        aria-label="RESET"
      >
        RESET
      </button>
      </div>`;
    }

    this.initSearchTabs();
  }

  setLiveRegionResults() {
    const minLoaderTime = 700;
    const elapsed = Date.now() - (this.loaderStartTime || 0);
    const remaining = Math.max(0, minLoaderTime - elapsed);

    setTimeout(() => {
      this.removeAttribute('loading');
    }, remaining);

    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = '100vh';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
    }

    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = '';

    if (this.resetButton) {
      this.resetButton.classList.add('hidden');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.querySelector('#predictive-search-option-search-keywords')?.classList.add('hidden');
    this.querySelector('.search_tab_content')?.classList.add('hidden');
  }

  // -------------------------- 
  // 🔹 Custom Tab Switch Logic
  // --------------------------
  initSearchTabs() {
    const tabsContainer = this.predictiveSearchResults.querySelector('.search_tab_content');
    if (!tabsContainer) return;

    const productTab = tabsContainer.querySelector('.product_tab');
    const collectionTab = tabsContainer.querySelector('.collection_tab');
    const pageTab = tabsContainer.querySelector('.page_tab');
    const productGroup = this.predictiveSearchResults.querySelector('.product_result_group');
    const suggestionGroup = this.predictiveSearchResults.querySelector('.predictive-search__result-group.suggestion');
    const pagesWrapper = this.predictiveSearchResults.querySelector('.predictive-search__pages-wrapper');

    const allSections = [productGroup, suggestionGroup, pagesWrapper];

    const hideAll = () => {
      allSections.forEach((el) => el?.classList.remove('active'));
    };

    const showSection = (el) => {
      if (el) el.classList.add('active');
    };

    const setActiveTab = (tab) => {
      tabsContainer.querySelectorAll('.tb').forEach((t) => t.classList.remove('active'));
      if (tab) tab.classList.add('active');
    };

    if (productTab) {
      productTab.addEventListener('click', (e) => {
        e.stopPropagation(); // ✅ Prevent modal from closing
        hideAll();
        showSection(productGroup);
        setActiveTab(productTab);
      });
    }

    if (collectionTab) {
      collectionTab.addEventListener('click', (e) => {
        e.stopPropagation(); // ✅ Prevent modal from closing
        hideAll();
        showSection(suggestionGroup);
        setActiveTab(collectionTab);
      });
    }

    if (pageTab) {
      pageTab.addEventListener('click', (e) => {
        e.stopPropagation(); // ✅ Prevent modal from closing
        hideAll();
        showSection(pagesWrapper);
        setActiveTab(pageTab);
      });
    }

    // Initial state
    hideAll();
    if (productGroup && productGroup.querySelectorAll('li, .predictive-search__item').length > 0) {
      showSection(productGroup);
      setActiveTab(productTab);
    } else if (suggestionGroup && suggestionGroup.querySelectorAll('li, .predictive-search__item').length > 0) {
      showSection(suggestionGroup);
      setActiveTab(collectionTab);
    } else if (pagesWrapper && pagesWrapper.querySelectorAll('li, .predictive-search__item').length > 0) {
      showSection(pagesWrapper);
      setActiveTab(pageTab);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);